<template>
  <base-drawer :show="show" title="Pay invoice" @close="onClose">
    <drawer-section title="Invoice" icon="icon-notes">
      <p class="invoice-line text-white">{{ invoice.description }}</p>
      <div class="detail-row text-white" v-if="invoice.invoice_id">
        <span>Invoice number</span>
        <span>#{{ invoice.invoice_id }}</span>
      </div>
      <div class="detail-row text-white" v-if="issuedDateDisplay">
        <span>Issued</span>
        <span>{{ issuedDateDisplay }}</span>
      </div>
      <div class="detail-row text-white" v-if="dueDateDisplay">
        <span>Due date</span>
        <span>{{ dueDateDisplay }}</span>
      </div>
      <hr class="invoice-divider" />
      <div class="detail-row detail-row--total text-white">
        <span>Amount due</span>
        <span>{{ amountDisplay }}</span>
      </div>
    </drawer-section>

    <drawer-section title="Card details" icon="icon-credit-card">
      <div class="row">
        <div class="col-12 col-sm-6">
          <base-input v-model="cardholderFirstName" label="Cardholder first name" placeholder="First name"></base-input>
        </div>
        <div class="col-12 col-sm-6">
          <base-input v-model="cardholderLastName" label="Cardholder last name" placeholder="Last name"></base-input>
        </div>
      </div>
      <form id="payment-form-square-pay">
        <div id="pay-card-container"></div>
      </form>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" :disabled="submitting" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="submitting || !cardReady" @click="submitPayment">
          Pay {{ amountDisplay }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate } from '@/plugins/dates'

const props = defineProps({
  show: { type: Boolean, default: false },
  invoice: { type: Object, default: () => ({}) },
  amountDisplay: { type: String, default: '' },
  squareAppId: { type: String, default: '' },
  squareLocationId: { type: String, default: '' }
})

const emit = defineEmits(['close', 'paid'])

const companyId = localStorage.getItem('companyId')

const cardholderFirstName = ref('')
const cardholderLastName = ref('')
const submitting = ref(false)
const cardReady = ref(false)

let payments: any = null
let card: any = null

const dueDateDisplay = computed(() => {
  return props.invoice && props.invoice.due_date ? formatGroupDate(props.invoice.due_date) : ''
})

const issuedDateDisplay = computed(() => {
  return props.invoice && props.invoice.created_at ? formatGroupDate(props.invoice.created_at) : ''
})

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function loadSquareScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).Square) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://web.squarecdn.com/v1/square.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Square.js failed to load'))
    document.body.appendChild(script)
  })
}

// Same style object the billing information tab uses, so both card forms match
// in dark and light mode.
function cardStyle() {
  return {
    '.input-container.is-focus': { borderColor: '#e14eca' },
    '.input-container.is-error': { borderColor: '#ff1600' },
    '.message-text.is-error': { color: '#ff1600' },
    '.message-icon.is-error': { color: '#ff1600' },
    'input.is-error': { color: '#ff1600' },
    '.message-icon': { color: '#adb5bd' },
    '@media screen and (max-width: 600px)': {
      input: { fontSize: '12px' }
    },
    '.input-container': { borderRadius: '6px' },
    '.message-text': { color: '#adb5bd' },
    input: {
      backgroundColor: localStorage.getItem('mode') === '0' ? '#ffffff' : '#22233a',
      color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff'
    },
    'input::placeholder': { color: '#adb5bd' }
  }
}

async function initCard() {
  try {
    await loadSquareScript()
    if (!(window as any).Square) {
      throw new Error('Square.js failed to load properly')
    }
    const container = document.getElementById('pay-card-container')
    if (!container || container.children.length > 0) {
      // Already attached; never attach twice, it appends a second iframe.
      cardReady.value = !!card
      return
    }
    payments = (window as any).Square.payments(props.squareAppId, props.squareLocationId)
    card = await payments.card({ style: cardStyle() })
    await card.attach('#pay-card-container')
    cardReady.value = true
  } catch (e) {
    console.error('Initializing card failed', e)
    swal('Error!', 'Could not load the card form. Please try again.', 'error')
  }
}

async function destroyCard() {
  cardReady.value = false
  if (card) {
    try {
      await card.destroy()
    } catch (e) {
      console.error(e)
    }
    card = null
  }
  const container = document.getElementById('pay-card-container')
  if (container) {
    container.innerHTML = ''
  }
}

async function tokenizeCard() {
  const tokenResult = await card.tokenize()
  if (tokenResult.status === 'OK') {
    return tokenResult
  }
  let errorMessage = `Tokenization failed with status: ${tokenResult.status}`
  if (tokenResult.errors) {
    errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`
  }
  throw new Error(errorMessage)
}

async function submitPayment() {
  if (submitting.value || !card) {
    return
  }
  submitting.value = true
  try {
    let tokenResult
    try {
      tokenResult = await tokenizeCard()
    } catch (e) {
      console.error(e)
      swal('Error!', 'Could not read your card details. Please check the card form and try again.', 'error')
      return
    }

    // Buyer verification amount must be a 2-decimal dollar string equal to the
    // server-charged amount (both derive from amount_due). A frictionless card
    // can resolve without a token; the backend treats it as optional.
    let verificationToken = null
    try {
      const verification = await payments.verifyBuyer(tokenResult.token, {
        intent: 'CHARGE',
        amount: parseFloat(props.invoice.amount_due).toFixed(2),
        currencyCode: 'USD',
        billingContact: {
          givenName: cardholderFirstName.value,
          familyName: cardholderLastName.value
        }
      })
      verificationToken = verification && verification.token ? verification.token : null
    } catch (e) {
      console.error('verifyBuyer failed', e)
      swal('Error!', 'Could not verify your card. Please try again.', 'error')
      return
    }

    const config = await authConfig()
    const url = `${API_LOCATION}companies/${companyId}/billing/invoices/${props.invoice.id}`
    const body: any = {
      token: tokenResult.token,
      cardholder_first_name: cardholderFirstName.value,
      cardholder_last_name: cardholderLastName.value
    }
    if (verificationToken) {
      body.verificationToken = verificationToken
    }

    try {
      await axios.post(url, body, config)
      swal('Success!', 'Payment received. Thank you!', 'success')
      emit('paid')
    } catch (err: any) {
      const status = err.response ? err.response.status : 0
      const data = err.response ? err.response.data : null
      if (status === 409) {
        swal('Invoice already settled', (data && data.error) || 'This invoice has already been settled.', 'warning')
        // The invoice state changed either way; let the parent refresh.
        emit('paid')
      } else if (status === 502 && data && data.square_error) {
        swal('Payment failed', data.square_error, 'error')
      } else if (status === 401) {
        swal('Not authorized', 'Only company admins can pay invoices.', 'error')
      } else {
        swal('Error!', (data && data.error) || 'Something went wrong', 'error')
      }
    }
  } finally {
    submitting.value = false
  }
}

function onClose() {
  if (submitting.value) {
    return
  }
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    cardholderFirstName.value = ''
    cardholderLastName.value = ''
    nextTick(() => initCard())
  } else {
    document.removeEventListener('keydown', onEscKey)
    destroyCard()
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
  destroyCard()
})
</script>

<style lang="scss" scoped>
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.invoice-line {
  font-size: 15px;
  margin-bottom: 12px;
}

// Canonical label/value row (same class + shape as BookingDrawer's detail-row).
.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-row--total {
  font-size: 15px;
  font-weight: 600;
}

// Light-mode counter, mirroring BookingDrawer's.
.white-content .detail-row {
  color: #525f7f;
}

// Theme-neutral divider; reads on both dark and light cards.
.invoice-divider {
  border: none;
  border-top: 1px solid rgba(125, 125, 125, 0.3);
  margin: 10px 0;
}

#pay-card-container {
  margin-top: 8px;
}
</style>
